import React, { useCallback, useEffect, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { SodInsights } from '../../../../shared/components';
import { useTypedSelector } from '../../../../shared/hooks';
import { type InsightItemTypes } from '../../../../shared/types';
import { createDevRejectionHandler } from '../../../../shared/utils';
import { entitlementActions } from '../../data-store';
import { useGetInsights } from '../../hooks';

interface EntitlementInsightsProps {
  className?: string;
}

/**
 * EntitlementInsights
 */
function EntitlementInsights({ className }: EntitlementInsightsProps) {
  const dispatch = useDispatch();

  const {
    aag: {
      global: { isGlobalLoading },
      entitlement: {
        controls: {
          isInsightsOpen,
          activeFilters,
          searchKey,
          sortInAction,
          activeInsight,
        },
      },
    },
  } = useTypedSelector((state) => state);

  const [insightsData, setInsightsData] = useState<InsightItemTypes[] | null>(
    null
  );

  const { handleFetchInsights, isError, isLoading } = useGetInsights();

  const handleInsightsFetch = useCallback(async () => {
    setInsightsData(null);

    try {
      const response = await handleFetchInsights(
        activeFilters,
        searchKey,
        sortInAction
      );

      setInsightsData(response);
    } catch (error) {
      Logger.log('Insights fetch error:', error);
    }
  }, [activeFilters, searchKey, sortInAction, handleFetchInsights]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isGlobalLoading) {
        dispatch(entitlementActions.setActiveInsight(null));
        handleInsightsFetch().catch(
          createDevRejectionHandler('EntitlementInsights: handleInsightsFetch')
        );
      }
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [dispatch, isGlobalLoading, handleInsightsFetch]);

  const handleIsInsights = useCallback(() => {
    dispatch(entitlementActions.setIsInsightsOpen(!isInsightsOpen));
  }, [dispatch, isInsightsOpen]);

  const handleOptionClick = useCallback(
    (insightItem: InsightItemTypes | null) => {
      batch(() => {
        dispatch(entitlementActions.setActiveInsight(insightItem));
        dispatch(entitlementActions.setListIsRefresh(true));
      });
    },
    [dispatch]
  );

  return (
    isInsightsOpen && (
      <SodInsights
        data={insightsData}
        isLoading={Boolean(isLoading || (!insightsData && !isError))}
        isError={isError}
        activeInsight={activeInsight}
        isInsightsOpen={isInsightsOpen}
        setIsInsightsOpen={handleIsInsights}
        handleRefresh={() => {
          handleInsightsFetch().catch(
            createDevRejectionHandler(
              'EntitlementInsights: handleInsightsFetch refresh'
            )
          );
        }}
        handleOptionClick={handleOptionClick}
        className={className}
      />
    )
  );
}

EntitlementInsights.defaultProps = {
  className: null,
};

export default EntitlementInsights;
